// IndexedDBService.js
// Lightweight, reusable IndexedDB service for autosave and offline storage
// Vanilla JS, no external libraries

import { withIdbRetry } from "./idbRetry";

const DB_NAME = "EwiDelAppIndexedDB";
const STORE_NAME = "autosave";
const DB_VERSION = 1;

// Simple flag to prevent saving during logout
let isLoggingOut = false;

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Compose a unique key for each user/entity/tab
export function makeKey({ userId, entityId, tab }) {
  return [userId, entityId, tab].filter(Boolean).join(":");
}

// Save data (with metadata) - skip if logging out
export async function saveData(key, data) {
  if (isLoggingOut) {
    return;
  }

  return withIdbRetry(
    async () => {
      const db = await openDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const now = new Date().toISOString();
        tx.objectStore(STORE_NAME).put({ key, data, updatedAt: now });
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    },
    undefined,
    "saveData"
  );
}

// Load data (returns {data, updatedAt} or null)
export async function loadData(key) {
  return withIdbRetry(
    async () => {
      const db = await openDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const req = tx.objectStore(STORE_NAME).get(key);
        req.onsuccess = () => {
          if (req.result) {
            resolve({ data: req.result.data, updatedAt: req.result.updatedAt });
          } else {
            resolve(null);
          }
        };
        req.onerror = () => reject(req.error);
      });
    },
    null,
    "loadData"
  );
}

// Delete data
export async function deleteData(key) {
  return withIdbRetry(
    async () => {
      const db = await openDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        tx.objectStore(STORE_NAME).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    },
    undefined,
    "deleteData"
  );
}

// Get all keys (for cleanup or listing drafts)
export async function getAllKeys() {
  return withIdbRetry(
    async () => {
      const db = await openDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.getAllKeys();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    },
    [],
    "getAllKeys"
  );
}

// Get metadata for a key (e.g., last updated)
export async function getMeta(key) {
  return withIdbRetry(
    async () => {
      const db = await openDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const req = tx.objectStore(STORE_NAME).get(key);
        req.onsuccess = () => {
          if (req.result) {
            resolve({ updatedAt: req.result.updatedAt });
          } else {
            resolve(null);
          }
        };
        req.onerror = () => reject(req.error);
      });
    },
    null,
    "getMeta"
  );
}

// Clear ALL data from IndexedDB (complete wipe)
export async function clearAllData() {
  return withIdbRetry(
    async () => {
      const db = await openDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        tx.objectStore(STORE_NAME).clear();
        tx.oncomplete = () => {
          resolve();
        };
        tx.onerror = () => reject(tx.error);
      });
    },
    undefined,
    "clearAllData"
  );
}

// Set logout flag to prevent saves
export function setLoggingOut(status) {
  isLoggingOut = status;
}
